/**
 * Upload with retry and main handoff. Per file: persist to SQLite first, then S3 with 3 fast retries;
 * on success mark uploaded; on failure enqueue for main.
 */

import S3Utils from '@renderer/utils/s3Utils'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import {
  hasPreloadAPI,
  saveFileToDB,
  markMediaUploaded,
  enqueueUpload
} from '@renderer/utils/pipeline/bridgeApi'
import type {
  ProcessedFile,
  ProcessingContext,
  UploadResult,
  ResourceType
} from '@renderer/types/outputPipeline.types'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'

const MAX_RETRIES = 3
const RETRY_DELAYS_MS = [1000, 2000, 4000]

function deriveResourceId(sessionId: string, fileName: string): string {
  const base = fileName.replace(/\.[^/.]+$/, '')
  return `${sessionId}_${base}`
}

function uploadTypeToResourceType(uploadType: ProcessedFile['uploadType']): ResourceType {
  if (uploadType === 'strip' || uploadType === 'image') return 'photo'
  if (uploadType === 'gif') return 'gif'
  return 'video'
}

function calculateExpirationDate(subscriptionType: string | null): Date {
  const now = new Date()
  switch (subscriptionType) {
    case 'enterprise':
      return new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
    case 'professional':
      return new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)
    case 'starter':
      return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    default:
      return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  }
}

interface S3Response {
  url: string
  key: string
  thumbnailUrl?: string
  metadata: {
    originalFilename: string
    contentType: string
    size: string
    width?: string
    height?: string
  }
}

async function uploadToS3(
  file: File,
  ctx: ProcessingContext
): Promise<S3Response> {
  const userId = ctx.context?.user?.email || ''
  const BAHELA_DEFAULT_PROJECT_ID = '691d305ea5b85a88793907bb'
  const projectId = ctx.projectId?.trim() ? ctx.projectId : BAHELA_DEFAULT_PROJECT_ID
  const response = await S3Utils.uploadFile(
    file,
    userId,
    projectId,
    ctx.sessionId,
    ctx.galleryId,
    ctx.eventId
  )
  return {
    url: response.url,
    key: response.key,
    thumbnailUrl: response.thumbnailUrl,
    metadata: response.metadata
  }
}

async function updateGalleryAPI(
  ctx: ProcessingContext,
  uploadType: ProcessedFile['uploadType'],
  response: S3Response
): Promise<void> {
  const subscriptionType = useSubscriptionStore.getState().type
  const expiresAt = calculateExpirationDate(subscriptionType)
  const mediaItem = {
    key: response.key,
    url: response.url,
    thumbnailUrl: response.thumbnailUrl,
    type: uploadType,
    createdAt: new Date(),
    expiresAt,
    metadata: {
      originalFilename: response.metadata.originalFilename,
      contentType: response.metadata.contentType,
      size: Number(response.metadata.size),
      ...(response.metadata.width && response.metadata.height && {
        width: Number(response.metadata.width),
        height: Number(response.metadata.height)
      })
    }
  }

  const BAHELA_DEFAULT_PROJECT_ID = '691d305ea5b85a88793907bb'
  const normalizedProjectId = ctx.projectId?.trim() ? ctx.projectId : BAHELA_DEFAULT_PROJECT_ID
  const galleryUrl = resolveGallerySessionUrlFromStore(ctx.sessionId)
  const createGalleryUrl = `${process.env.VITE_API_URL}/api/gallery`

  const res = await fetch(createGalleryUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      sessionId: ctx.sessionId,
      userId: ctx.context?.user?.email || '',
      projectId: normalizedProjectId,
      eventId: ctx.eventId,
      mediaItem,
      url: galleryUrl,
      token: useSettingsStore.getState().activationCode || undefined,
      deviceId: useSettingsStore.getState().deviceId || undefined
    })
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Gallery update failed: ${res.status} ${text}`)
  }
}

/**
 * Single-file upload: persist to SQLite first (when preload exists), then S3 with 3 retries;
 * on success mark uploaded and update gallery; on failure hand off to main.
 */
export async function uploadFile(
  processed: ProcessedFile,
  ctx: ProcessingContext
): Promise<UploadResult> {
  const localUrl = URL.createObjectURL(processed.file)

  if (ctx.downloadLocal) {
    const a = document.createElement('a')
    a.href = localUrl
    a.download = processed.file.name
    a.setAttribute('data-automated-click', 'true')
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const hasPreload = hasPreloadAPI()
  let resourceId: string | undefined

  if (hasPreload) {
    const arrayBuffer = await processed.file.arrayBuffer()
    const saveResult = await saveFileToDB({
      sessionId: ctx.sessionId,
      fileName: processed.fileName,
      fileType: processed.fileType,
      data: arrayBuffer,
      metadata: { ...processed.metadata, alreadyUploaded: false }
    })
    if (saveResult.skipped || !saveResult.success) {
      // Continue without SQLite; no handoff to main on failure
    } else {
      resourceId = saveResult.resourceId ?? deriveResourceId(ctx.sessionId, processed.fileName)
    }
  }

  if (!ctx.context?.user?.email) {
    return { localUrl }
  }

  let lastError: Error | null = null
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await uploadToS3(processed.file, ctx)
      try {
        await updateGalleryAPI(ctx, processed.uploadType, response)
      } catch (galleryErr) {
        console.warn('[uploadQueue] Gallery update failed, S3 upload succeeded:', galleryErr)
      }
      if (hasPreload && resourceId) {
        await markMediaUploaded({
          sessionId: ctx.sessionId,
          resourceId,
          resourceType: uploadTypeToResourceType(processed.uploadType),
          fileName: processed.fileName
        })
      }
      return { localUrl, uploadedUrl: response.url, s3Key: response.key }
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))
      console.error('[uploadQueue] Error uploading file:', lastError.message)
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]))
      }
    }
  }

  if (hasPreload && resourceId) {
    await enqueueUpload({ sessionId: ctx.sessionId, resourceId })
  }

  return { localUrl }
}
